import { getCurrentUser } from "aws-amplify/auth";
import mqtt from "mqtt";
import { Guid } from "typescript-guid";
import {
	computed,
	EffectScope,
	getCurrentScope,
	onScopeDispose,
	readonly,
	ref,
	shallowRef,
	unref,
	watchEffect,
	type ComputedRef,
	type Ref,
} from "vue";
import { buildAwsWebsocketUrl } from "./aws-credentials";
import type {
	BrokerConfig,
	ConnectionStatus,
	MqttJsClient,
	MqttPublishOptions,
	RequestResponseOptions,
} from "./types";

const CREDENTIALS_REFRESH_INTERVAL_MS = 50 * 60 * 1000; // 50 minutes
const CREDENTIALS_RETRY_INTERVAL_MS = 5_000;
const DEFAULT_RECONNECT_PERIOD_MS = 1_000;
const CORRELATION_ID_RANDOM_LENGTH = 9;
const WILDCARD_CACHE_MAX = 500;

// Shared TextDecoder instance, used to decode request/response correlation ids
const textDecoder = /* @__PURE__ */ new TextDecoder();

interface PendingRequest {
	resolve: (value: unknown) => void;
	reject: (error: Error) => void;
	timer?: ReturnType<typeof setTimeout>;
	/** Cleans up the response topic subscription */
	cleanup: () => void;
}

const wildcardRegexCache = new Map<string, RegExp>();

function matchesMqttWildcard(topic: string, pattern: string): boolean {
	if (pattern === topic) return true;
	if (pattern === "#") return true;
	if (!pattern.includes("+") && !pattern.includes("#")) return false;

	let regex = wildcardRegexCache.get(pattern);
	if (!regex) {
		// Evict oldest entry if cache is full
		if (wildcardRegexCache.size >= WILDCARD_CACHE_MAX) {
			wildcardRegexCache.delete(wildcardRegexCache.keys().next().value!);
		}
		const escaped = pattern
			.replace(/[.*?^${}()|[\]\\]/g, "\\$&")
			.replace(/\+/g, "[^/]+")
			.replace(/\/?#$/g, "(?:/.*)?");
		regex = new RegExp(`^${escaped}$`);
		wildcardRegexCache.set(pattern, regex);
	}
	return regex.test(topic);
}

/**
 * Creates a stateful MQTT client backed by mqtt.js: connection lifecycle,
 * topic subscriptions (exact + wildcard), request/response correlation, and
 * reconnecting to a different broker in place (see `reconnect`).
 */
export function createMqttClient(initialConfig: BrokerConfig): MqttJsClient {
	// Reassigned by reconnect() to point subsequent connect() calls at a new
	// broker while keeping this same client object (and its subscriptions).
	let config = initialConfig;

	const client = shallowRef<mqtt.MqttClient>();
	const isConnected = ref(false);
	const isConnecting = ref(false);
	const error = ref<string>();

	const connectionStatus = computed<ConnectionStatus>(() => {
		if (isConnecting.value) return "connecting";
		if (isConnected.value) return "connected";
		return "disconnected";
	});

	type MessageCallback = (
		topic: string,
		payload: unknown,
		packet: mqtt.IPublishPacket,
	) => void;

	// Separate exact-match and wildcard filters for O(1) exact lookup
	const exactCallbacks = new Map<string, Set<MessageCallback>>();
	const wildcardCallbacks = new Map<string, Set<MessageCallback>>();
	/** Tracks the highest QoS requested per filter for reconnect re-subscribes */
	const filterQos = new Map<string, 0 | 1 | 2>();

	const pendingRequests = new Map<string, PendingRequest>();

	let credentialsRefreshTimer: ReturnType<typeof setInterval> | undefined;
	let credentialsRetryTimer: ReturnType<typeof setTimeout> | undefined;
	let visibilityHandler: (() => void) | undefined;
	/**
	 * Bumped on every connect() attempt and on every disconnect(). Lets a
	 * connect() call detect, after an await, that a newer connect()/
	 * reconnect() call has since taken over - so it can abandon itself
	 * instead of either overwriting the newer call's connection (client.value)
	 * or clobbering state the newer call already owns (isConnecting, error,
	 * credential timers).
	 */
	let connectGeneration = 0;

	const connect = async (): Promise<void> => {
		if (isConnected.value || isConnecting.value) return;

		// config can be reassigned by reconnect() while this call is still
		// awaiting below, so snapshot it now - every use of activeConfig below
		// refers to the broker this specific connect() call is actually
		// connecting to, not whatever config happens to be current later.
		const activeConfig = config;
		const attemptGeneration = ++connectGeneration;

		console.info(
			`[mqtt-client] connect() attempt #${attemptGeneration} starting.`,
		);

		isConnecting.value = true;
		error.value = undefined;

		try {
			let url: string;
			let latestAwsUrl = "";
			const mqttOptions: mqtt.IClientOptions = {
				reconnectPeriod: DEFAULT_RECONNECT_PERIOD_MS,
			};

			if (activeConfig.type === "local") {
				url = activeConfig.url;
				if (activeConfig.username)
					mqttOptions.username = activeConfig.username;
				if (activeConfig.password)
					mqttOptions.password = activeConfig.password;
				mqttOptions.clientId =
					activeConfig.clientId ??
					`outrun-${Guid.create().toString()}`;
				mqttOptions.protocolVersion = 4;
			} else {
				// AWS IoT Core via Cognito websocket
				const user = await getCurrentUser();
				url = await buildAwsWebsocketUrl(activeConfig);

				const prefix = activeConfig.clientIdPrefix ?? "portal";
				mqttOptions.clientId = `${prefix}-${user.userId}-${Guid.create().toString()}`;
				mqttOptions.protocolVersion = activeConfig.protocolVersion ?? 4;
				mqttOptions.wsOptions = { protocol: "mqtt" };
				mqttOptions.transformWsUrl = () => latestAwsUrl;
			}

			// Stale attempt - a newer call has taken over since we started
			// awaiting credentials (see connectGeneration above). Abandon
			// without touching anything the newer attempt now owns.
			if (attemptGeneration !== connectGeneration) {
				console.warn(
					`[mqtt-client] connect() attempt #${attemptGeneration} was superseded by a newer ` +
						`call (now at #${connectGeneration}) while awaiting credentials - abandoning it.`,
				);
				return;
			}

			latestAwsUrl = url;

			const mqttClient = mqtt.connect(url, mqttOptions);
			client.value = mqttClient;

			mqttClient.on("connect", () => {
				// Guard against stale client after disconnect() during async connect
				if (client.value !== mqttClient) return;

				console.info("[mqtt-client] Connected to broker");
				isConnected.value = true;
				isConnecting.value = false;
				error.value = undefined;

				// Start credential refresh only after successful connection
				if (activeConfig.type === "aws" && !credentialsRefreshTimer) {
					credentialsRefreshTimer = setInterval(async () => {
						try {
							latestAwsUrl =
								await buildAwsWebsocketUrl(activeConfig);
						} catch (e) {
							console.error(
								"Failed to refresh AWS IoT credentials:",
								e,
							);
						}
					}, CREDENTIALS_REFRESH_INTERVAL_MS);
				}

				// Refresh credentials when tab becomes visible
				if (activeConfig.type === "aws" && !visibilityHandler) {
					visibilityHandler = () => {
						if (document.visibilityState !== "visible") return;
						buildAwsWebsocketUrl(activeConfig)
							.then(url => {
								latestAwsUrl = url;
								console.info(
									"[mqtt-client] Refreshed AWS credentials on visibility change",
								);
							})
							.catch(e => {
								console.error(
									"[mqtt-client] Failed to refresh credentials on visibility change:",
									e,
								);
							});
					};
					document.addEventListener(
						"visibilitychange",
						visibilityHandler,
					);
				}

				// Re-subscribe all tracked filters after reconnect with stored QoS
				for (const filter of exactCallbacks.keys()) {
					mqttClient.subscribe(filter, {
						qos: filterQos.get(filter) ?? 0,
					});
				}
				for (const filter of wildcardCallbacks.keys()) {
					mqttClient.subscribe(filter, {
						qos: filterQos.get(filter) ?? 0,
					});
				}
			});

			mqttClient.on("error", (err: Error) => {
				if (client.value !== mqttClient) return;
				console.error("[mqtt-client] Error:", err);
				error.value = err.message;
			});

			mqttClient.on("close", () => {
				if (client.value !== mqttClient) return;
				console.info("[mqtt-client] Disconnected from broker");
				isConnected.value = false;
			});

			mqttClient.on("reconnect", () => {
				if (client.value !== mqttClient) return;
				console.info("[mqtt-client] Reconnecting to broker...");
				isConnecting.value = true;

				// Eagerly refresh credentials before re-connect
				if (activeConfig.type === "aws") {
					buildAwsWebsocketUrl(activeConfig)
						.then(url => {
							latestAwsUrl = url;
							console.info(
								"[mqtt-client] Refreshed AWS credentials on reconnect",
							);
						})
						.catch(e => {
							console.error(
								"[mqtt-client] Failed to refresh credentials on reconnect:",
								e,
							);
						});
				}
			});

			mqttClient.on(
				"message",
				(
					topic: string,
					payloadBuf: Buffer,
					packet: mqtt.IPublishPacket,
				) => {
					const payloadStr = payloadBuf.toString("utf-8");
					const parsed: unknown = JSON.parse(payloadStr);

					const pub = packet as mqtt.IPublishPacket;

					// Check request-response correlation
					const corrData = pub.properties?.correlationData;
					if (corrData) {
						let corrId: string | undefined;
						try {
							corrId =
								typeof corrData === "string"
									? corrData
									: textDecoder.decode(corrData);
						} catch (e) {
							console.error(
								"[mqtt-client] Failed to decode correlationData:",
								e,
							);
						}
						if (corrId) {
							const pending = pendingRequests.get(corrId);
							if (pending) {
								if (pending.timer) clearTimeout(pending.timer);
								pendingRequests.delete(corrId);
								pending.cleanup();
								pending.resolve(parsed);
								return;
							}
						}
					}

					// Fast path: exact-match subscribers (O(1))
					const exactSet = exactCallbacks.get(topic);
					if (exactSet) {
						for (const cb of exactSet) {
							try {
								cb(topic, parsed, pub);
							} catch (e) {
								console.error("Subscriber callback error:", e);
							}
						}
					}

					// Wildcard subscribers
					for (const [filter, cbs] of wildcardCallbacks) {
						if (!matchesMqttWildcard(topic, filter)) continue;
						for (const cb of cbs) {
							try {
								cb(topic, parsed, pub);
							} catch (e) {
								console.error("Subscriber callback error:", e);
							}
						}
					}
				},
			);
		} catch (err) {
			// Same staleness check as above - this failure belongs to an
			// attempt a newer call has already superseded, so isConnecting/
			// error/timers below may belong to that newer attempt now.
			if (attemptGeneration !== connectGeneration) {
				console.warn(
					`[mqtt-client] connect() attempt #${attemptGeneration} failed after being superseded ` +
						`by a newer call (now at #${connectGeneration}) - ignoring.`,
				);
				return;
			}

			isConnecting.value = false;
			error.value =
				err instanceof Error ? err.message : "Connection failed";

			// Clean up credential timer if it was started before failure
			if (credentialsRefreshTimer) {
				clearInterval(credentialsRefreshTimer);
				credentialsRefreshTimer = undefined;
			}

			if (activeConfig.type === "aws") {
				credentialsRetryTimer = setTimeout(
					() => connect(),
					CREDENTIALS_RETRY_INTERVAL_MS,
				);
			} else {
				throw err;
			}
		}
	};

	const disconnect = (): void => {
		connectGeneration++;
		console.info(
			"[mqtt-client] disconnect() called - ending current connection.",
		);

		if (credentialsRefreshTimer) {
			clearInterval(credentialsRefreshTimer);
			credentialsRefreshTimer = undefined;
		}
		if (credentialsRetryTimer) {
			clearTimeout(credentialsRetryTimer);
			credentialsRetryTimer = undefined;
		}
		if (visibilityHandler) {
			document.removeEventListener("visibilitychange", visibilityHandler);
			visibilityHandler = undefined;
		}

		for (const req of pendingRequests.values()) {
			if (req.timer) clearTimeout(req.timer);
			req.cleanup(); // prevent response topic subscription leak
			req.reject(new Error("Connection closed"));
		}
		pendingRequests.clear();

		client.value?.end(true);
		client.value = undefined;
		isConnected.value = false;
		isConnecting.value = false;
	};

	const validateTopic = (topic: string, allowWildcards: boolean): void => {
		if (!topic) {
			throw new Error("MQTT topic must not be empty");
		}
		if (topic.includes("\0")) {
			throw new Error("MQTT topic must not contain null characters");
		}
		if (!allowWildcards && (topic.includes("+") || topic.includes("#"))) {
			throw new Error("MQTT publish topic must not contain wildcards");
		}
	};

	const publish = (
		topic: string,
		payload: unknown,
		options?: MqttPublishOptions,
	): void => {
		if (!client.value || !isConnected.value) {
			throw new Error("MQTT client not connected");
		}
		validateTopic(topic, false);
		const data =
			typeof payload === "string" ? payload : JSON.stringify(payload);
		client.value.publish(topic, data, {
			qos: options?.qos ?? 0,
			retain: options?.retain ?? false,
			properties: options?.properties,
		});
	};

	const addCallback = (
		filter: string,
		cb: MessageCallback,
		qos: 0 | 1 | 2 = 0,
	): (() => void) => {
		validateTopic(filter, true);
		const isWildcard = filter.includes("+") || filter.includes("#");
		const map = isWildcard ? wildcardCallbacks : exactCallbacks;

		let set = map.get(filter);
		const existingQos = filterQos.get(filter);
		if (!set) {
			set = new Set();
			map.set(filter, set);
			filterQos.set(filter, qos);
			if (client.value && isConnected.value) {
				client.value.subscribe(filter, { qos });
			}
		} else if (existingQos !== undefined && qos > existingQos) {
			// Upgrade QoS if a higher level is requested
			filterQos.set(filter, qos);
			if (client.value && isConnected.value) {
				client.value.subscribe(filter, { qos });
			}
		}
		set.add(cb);

		return () => {
			const s = map.get(filter);
			if (s) {
				s.delete(cb);
				if (s.size === 0) {
					map.delete(filter);
					filterQos.delete(filter);
					client.value?.unsubscribe(filter);
				}
			}
		};
	};

	// Returns a readonly ref of the latest payload for a topic
	const subscribeMqttMessage = <T = unknown>(
		topic:
			| string
			| Ref<string | undefined>
			| ComputedRef<string | undefined>,
		qos: 0 | 1 | 2 = 0,
		scope?: EffectScope,
	): Readonly<Ref<T | undefined>> => {
		const data = shallowRef<T | undefined>(undefined);
		let removeCallback: (() => void) | undefined;
		let currentFilter: string | undefined;

		const setup = (filter: string) => {
			if (filter === currentFilter) return;
			cleanup();
			currentFilter = filter;
			removeCallback = addCallback(
				filter,
				(_topic, payload) => {
					data.value = payload as T;
				},
				qos,
			);
		};

		const cleanup = () => {
			if (!removeCallback) return;
			removeCallback();
			removeCallback = undefined;
			currentFilter = undefined;
		};

		const stopWatcher = watchEffect(() => {
			const t = typeof topic === "string" ? topic : topic.value;
			if (t) {
				setup(t);
				return;
			}

			cleanup();
			data.value = undefined;
		});

		const dispose = () => {
			stopWatcher();
			cleanup();
		};

		if (scope) {
			scope.run(() => {
				onScopeDispose(dispose);
			});
		} else if (getCurrentScope()) {
			onScopeDispose(dispose);
		} else if (import.meta.env.DEV) {
			console.warn(
				"[mqtt-client] subscribeMqttMessage called outside a Vue effect scope — " +
					"the returned subscription will never be cleaned up automatically.",
			);
		}

		return readonly(data) as Readonly<Ref<T | undefined>>;
	};

	// Returns reactive state with data + topic + timestamp for a topic
	const subscribeMqttTopic = <T = unknown>(
		topic:
			| string
			| Ref<string | undefined>
			| ComputedRef<string | undefined>,
		qos: 0 | 1 | 2 = 0,
		scope?: EffectScope,
	): {
		data: Readonly<Ref<T | undefined>>;
		topic: Readonly<Ref<string | undefined>>;
		timestamp: Readonly<Ref<number | undefined>>;
	} => {
		// Single shallowRef to batch all three values into one reactivity trigger
		const state = shallowRef<{
			data?: T;
			topic?: string;
			timestamp?: number;
		}>({ data: undefined, topic: undefined, timestamp: undefined });

		let removeCallback: (() => void) | undefined;
		let currentFilter: string | undefined;

		const setup = (filter: string) => {
			if (filter === currentFilter) return;
			cleanup();
			currentFilter = filter;
			removeCallback = addCallback(
				filter,
				(t, payload) => {
					state.value = {
						data: payload as T,
						topic: t,
						timestamp: Date.now(),
					};
				},
				qos,
			);
		};

		const cleanup = () => {
			if (!removeCallback) return;
			removeCallback();
			removeCallback = undefined;
			currentFilter = undefined;
			state.value = {
				data: undefined,
				topic: undefined,
				timestamp: undefined,
			};
		};

		const stopWatcher = watchEffect(() => {
			const t = unref(topic);
			if (t) setup(t);
			else cleanup();
		});

		const dispose = () => {
			stopWatcher();
			cleanup();
		};

		if (scope) {
			scope.run(() => {
				onScopeDispose(dispose);
			});
		} else if (getCurrentScope()) {
			onScopeDispose(dispose);
		} else if (import.meta.env.DEV) {
			console.warn(
				"[mqtt-client] subscribeMqttTopic called outside a Vue effect scope — " +
					"the returned subscription will never be cleaned up automatically.",
			);
		}

		return {
			data: computed(() => state.value.data),
			topic: computed(() => state.value.topic),
			timestamp: computed(() => state.value.timestamp),
		};
	};

	const request = async <T = unknown>(
		requestTopic: string,
		payload: unknown,
		options: RequestResponseOptions = {},
	): Promise<T> => {
		if (!client.value || !isConnected.value) {
			throw new Error("MQTT client not connected");
		}

		const correlationId =
			options.correlationId ??
			`${Date.now()}-${Math.random()
				.toString(36)
				.slice(2, 2 + CORRELATION_ID_RANDOM_LENGTH)}`;
		const responseTopic =
			options.responseTopic ?? `response/${correlationId}`;

		// Subscribe to response topic — cleanup is stored so disconnect() can call it
		const removeResponseCb = addCallback(responseTopic, () => {
			/* handled via pendingRequests */
		});

		return new Promise<T>((resolve, reject) => {
			let timer: ReturnType<typeof setTimeout> | undefined;

			if (options.timeout && options.timeout > 0) {
				timer = setTimeout(() => {
					pendingRequests.delete(correlationId);
					removeResponseCb();
					reject(
						new Error(`Request timeout after ${options.timeout}ms`),
					);
				}, options.timeout);
			}

			pendingRequests.set(correlationId, {
				resolve: resolve as (value: unknown) => void,
				reject,
				timer,
				cleanup: removeResponseCb,
			});

			const data =
				typeof payload === "string" ? payload : JSON.stringify(payload);

			client.value!.publish(requestTopic, data, {
				qos: options.qos ?? 0,
				properties: {
					correlationData: Buffer.from(correlationId, "utf-8"),
					responseTopic,
				},
			});
		});
	};

	const reconnect = async (newConfig: BrokerConfig): Promise<void> => {
		console.info(
			"[mqtt-client] reconnect() called - moving shared connection to a new broker.",
		);

		// exactCallbacks/wildcardCallbacks/filterQos belong to this closure, not
		// to the underlying mqtt.MqttClient, so disconnect() doesn't touch them -
		// the "connect" handler's existing re-subscribe-all-tracked-filters loop
		// picks them up again once the new connection is up.
		disconnect();
		config = newConfig;
		await connect();

		// connect() only awaits kicking off the new connection, not the actual
		// handshake - by this point the client is still typically "connecting",
		// not yet "connected". The "Connected to broker" log fires separately
		// once that actually happens.
		console.info(
			"[mqtt-client] reconnect() returned - new connection attempt is in progress.",
		);
	};

	return {
		client: readonly(client) as unknown as Readonly<
			Ref<mqtt.MqttClient | undefined>
		>,
		isConnected: readonly(isConnected),
		isConnecting: readonly(isConnecting),
		connectionStatus,
		error: readonly(error),
		connect,
		disconnect,
		publish,
		subscribeMqttMessage,
		subscribeMqttTopic,
		request,
		addCallback,
		reconnect,
	};
}
